import { X } from 'lucide-react';

export interface ActiveChip {
  key: string;
  /** Human-readable summary, e.g. "Study: ATLAS-2". */
  label: string;
  onRemove: () => void;
}

interface Props {
  chips: ActiveChip[];
  /** Single reset that clears EVERYTHING (quick chips + panel filters). */
  onClearAll?: () => void;
}

/**
 * Dismissible summary of the applied filters plus one Clear-all. Makes the
 * applied state visible without reopening any panel, and gives every view the
 * same single reset. Renders nothing when no filters are applied.
 */
export default function ActiveFilters({ chips, onClearAll }: Props) {
  if (chips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Filters</span>
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-1 pl-2.5 pr-1 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
        >
          {c.label}
          <button
            type="button"
            onClick={c.onRemove}
            aria-label={`Remove filter ${c.label}`}
            className="rounded-full p-0.5 text-primary-600 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      {onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        >
          <X className="h-3.5 w-3.5" /> Clear all
        </button>
      )}
    </div>
  );
}
